import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { cloneDeep } from 'lodash'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../../components/ModalContainer/ModelContainer'
import Icon from '../../../../components/Icons'
import Stepper from '../../../../components/Stepper/Stepper'
import ConfirmPopup from '../../../../components/Popups/ConfirmPopup'
import { createCustomTask } from '../../../../reducers/Organisation/checklistsReducer'
import { INITIAL_TASK_STATE, formatResidentStatusesForFilter, validateForm } from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { selectUserRole } from '../../../../reducers/userDataReducer'
import api from '../../../../api/api'

import TaskSettings from './steps/TaskSettings'
import TaskData from './steps/TaskData'

const ALREADY_EXISTS_ERROR = 'Task with the same name already exists'

const ChecklistsCreateCustomTaskModal = ({
  onBack = () => {},
  onClose = () => {},
  createCustomTask = () => {},
  selectedBuilding,
  getAmenities,
  amenities,
  userRole,
}) => {
  const { t } = useTranslation(['checklists-create-task'])
  const isAdmin = userRole === 'Administrator'
  const [currentStep, setCurrentStep] = useState(0)
  const [formState, setFormState] = useState(INITIAL_TASK_STATE)
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmPopupVisible, setConfirmPopupVisible] = useState(false)
  const [availableStatuses, setAvailableStatuses] = useState([])
  const [nameError, setNameError] = useState('')
  const [noInputsError, setNoInputsError] = useState('')
  const { displaySnackbar } = useSnackbar()

  const [yardiCustomRecords, setYardiCustomRecords] = useState(null)

  const isValidForm = () =>
    validateForm({
      formState,
      setFormState,
      setNameError,
      setNoInputsError,
      t,
    })

  const handleBack = () => {
    if (!currentStep) {
      setConfirmPopupVisible(true)
    } else {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const onHandleNext = () => {
    if (isValidForm()) {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleFieldChange = ({ target: { name, value } }) => {
    if (name === 'serviceTask' && value === true) {
      setFormState((prev) => ({
        ...prev,
        serviceTask: true,
        dueDateTriggerVisibility: false,
        approvalRequired: false,
        signatureRequired: false,
      }))
      return
    }
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const onSubmit = async () => {
    setIsLoading(true)
    try {
      const {
        name,
        attachment,
        instructions,
        dueDateDays,
        dueDateTrigger,
        dueDateCondition,
        dueDateTriggerVisibility,
        assignTaskTrigger,
        assignTaskDays,
        assignTaskRelative,
        assignTriggerVisibility,
        approvalRequired,
        signatureRequired,
        limitTaskAssignmentVisibility,
        assignTaskStatusTrigger,
        amenityId,
        inputs,
        serviceTask,
      } = cloneDeep(formState)

      const updatedInputs = inputs.map((input) => {
        if (input.providersInfo && input.providersInfo.length) {
          input.providersInfo = input.providersInfo.map((provider) => provider.id)
        }
        if (
          input.inputType === 'realpage_pet' ||
          input.inputType === 'realpage_vehicle' ||
          input.inputType === 'vehicle_form'
        ) {
          input.isRequired = true
        }
        input.yardiOtherOccupantCustomRecord = input.yardiOtherOccupantCustomRecord
          ? input.yardiOtherOccupantCustomRecord.id
          : null
        input.yardiResidentCustomRecord = input.yardiResidentCustomRecord
          ? input.yardiResidentCustomRecord.id
          : null
        delete input._id
        if (input.form && input.form.length) {
          input.form = input.form.map(({ _id: _unused, ...rest }) => rest)
        }
        return input
      })

      const requestData = {
        buildingId: selectedBuilding ? selectedBuilding.id : undefined,
        name,
        instructions,
        attachment,
        ...(dueDateTriggerVisibility && { dueDateDays, dueDateTrigger, dueDateCondition }),
        ...(assignTriggerVisibility && { assignTaskTrigger, assignTaskDays, assignTaskRelative }),
        assignTaskStatusTrigger: limitTaskAssignmentVisibility ? assignTaskStatusTrigger : null,
        approvalRequired,
        signatureRequired,
        assignTriggerVisibility,
        dueDateTriggerVisibility,
        inputs: updatedInputs,
        amenityId,
        serviceTask,
      }
      await createCustomTask(requestData)
      displaySnackbar(t('checklists-create-task:customTaskModal.createSuccess'))
      onClose()
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message
      if (message) {
        displaySnackbar(message, true)
        if (message === ALREADY_EXISTS_ERROR) {
          setNameError(ALREADY_EXISTS_ERROR)
          setCurrentStep(0)
        } else {
          onClose()
        }
      } else {
        displaySnackbar(error.message, true)
        onClose()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const steps = [
    {
      Component: TaskData,
      title: t('checklists-create-task:taskSteps.taskData'),
      config: {
        formState,
        nameError,
        noInputsError,
        handleFieldChange,
        onHandleNext,
        selectedBuilding: selectedBuilding,
        yardiCustomRecords: yardiCustomRecords,
        setYardiCustomRecords: setYardiCustomRecords,
        setNameError,
      },
    },
    {
      Component: TaskSettings,
      title: t('checklists-create-task:taskSteps.taskSettings'),
      config: {
        formState,
        isLoading,
        availableStatuses,
        handleFieldChange,
        handleBack,
        onSubmit,
        getAmenities,
        amenities,
        buildingId: selectedBuilding.id,
        isAdmin,
      },
    },
  ]

  const fetchAvailableStatuses = async () => {
    try {
      const { data } = await api.fetchResidentStatuses()
      setAvailableStatuses(formatResidentStatusesForFilter(data))
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  const renderStep = () => {
    const { Component, config } = steps[currentStep]
    return <Component onNext={onHandleNext} onBack={handleBack} {...config} />
  }

  useEffect(() => {
    fetchAvailableStatuses()
    api
      .getYardiCustomRecordsByBuildingId(selectedBuilding.id)
      .then((response) => {
        setYardiCustomRecords(response.data)
      })
      // Fetched for every building; Yardi sync is optional, so a failure leaves it null
      .catch(() => {})
  }, [])

  return (
    <React.Fragment>
      <ModalContainer
        additionalClass="checklist-modal"
        onClose={() => setConfirmPopupVisible(true)}
        headerTitle={t('checklists-create-task:customTaskModal.headerTitle')}
        headerIcon={<Icon icon="checkClipboard" />}
      >
        <div>
          <Stepper currentStep={currentStep} steps={steps.map((step) => step.title)} />
          {renderStep()}
        </div>
      </ModalContainer>
      <ConfirmPopup
        title={t('checklists-create-task:shared.cautionTitle')}
        body={t('checklists-create-task:shared.discardChangesBody')}
        cancelText={t('checklists-create-task:shared.backToTaskButton')}
        confirmText={t('checklists-create-task:shared.discardChangesButton')}
        isOpen={isConfirmPopupVisible}
        cancelCallback={() => setConfirmPopupVisible(false)}
        confirmCallback={onBack}
        className={'is-caution'}
      />
    </React.Fragment>
  )
}

ChecklistsCreateCustomTaskModal.propTypes = {
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  createCustomTask: PropTypes.func,
  selectedBuilding: PropTypes.object,
  getAmenities: PropTypes.func,
  amenities: PropTypes.array,
  userRole: PropTypes.string,
}

const mapStateToProps = (state) => ({
  userRole: selectUserRole(state),
})

export default connect(mapStateToProps, { createCustomTask })(ChecklistsCreateCustomTaskModal)
